import { useEffect, useState, FormEvent } from 'react';
import { Modal } from './Modal';

interface Props {
  open: boolean;
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  /** Called with the typed password on submit. Resolve any work, then close. */
  onConfirm: (password: string) => void | Promise<void>;
  onCancel: () => void;
  /** If supplied, shown as an error banner inside the modal (e.g. "Wrong password"). */
  error?: string | null;
  busy?: boolean;
}

/**
 * Password-confirm modal for destructive actions (e.g. site deletion).
 * Native window.prompt isn't HTTPS-styled, paste-safe on iOS, or visually
 * trusted — exactly when "wait, is this a phishing tab?" kicks in. This
 * keeps it on-brand and lets us add a proper masked input + autoComplete.
 */
export function PasswordPromptModal({
  open, title, body, confirmLabel = 'Confirm',
  onConfirm, onCancel, error, busy,
}: Props) {
  const [password, setPassword] = useState('');

  // Clear the field every time the modal opens.
  useEffect(() => {
    if (open) setPassword('');
  }, [open]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!password.trim() || busy) return;
    await onConfirm(password);
  }

  return (
    <Modal open={open} title={title} onClose={() => { if (!busy) onCancel(); }}>
      <form onSubmit={onSubmit}>
        {body && (
          <div style={{ marginBottom: 14, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
            {body}
          </div>
        )}
        <div className="field">
          <label htmlFor="pwd-confirm">Password</label>
          <input
            id="pwd-confirm"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            required
          />
        </div>
        {error && <div className="banner banner-error">{error}</div>}
        <div className="btn-stack" style={{ marginTop: 14 }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !password.trim()}
            style={{ background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' }}
          >
            {busy ? 'Checking…' : confirmLabel}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}
